import { registerPlugin } from '@capacitor/core';
import { isNativePlatform } from './platform';

/**
 * Speakerphone / audio-route control (iOS native).
 *
 * ⚠️ `setSinkId()` — the web way to pick an audio output — is NOT implemented
 * in WebKit, so on iOS the app previously had no working way to move call audio
 * off the earpiece. This talks to AudioSessionPlugin.swift, which drives
 * AVAudioSession directly.
 *
 * Every function no-ops safely on web and on Android, where the browser/OS
 * already routes call audio sensibly.
 */
export interface AudioSessionPlugin {
  configureForCall(): Promise<{ route: string }>;
  setSpeakerphone(options: { enabled: boolean }): Promise<{ enabled: boolean; route: string }>;
  isSpeakerphoneOn(): Promise<{ enabled: boolean; route: string }>;
  getCurrentRoute(): Promise<{ route: string }>;
  deactivate(): Promise<void>;
}

let plugin: AudioSessionPlugin | null = null;

function get(): AudioSessionPlugin | null {
  if (!isNativePlatform()) return null;
  if (!plugin) {
    try {
      plugin = registerPlugin<AudioSessionPlugin>('AudioSession');
    } catch {
      return null;
    }
  }
  return plugin;
}

/** True when a real speaker toggle is available — used to hide the button on web. */
export function supportsSpeakerphone(): boolean {
  return isNativePlatform();
}

/** Call when a call connects: sets .playAndRecord/.voiceChat and activates. */
export async function configureAudioForCall(): Promise<void> {
  try {
    await get()?.configureForCall();
  } catch {
    /* audio still works on the default route — never block a call on this */
  }
}

export async function setSpeakerphone(enabled: boolean): Promise<boolean> {
  try {
    const r = await get()?.setSpeakerphone({ enabled });
    return r?.enabled ?? false;
  } catch {
    return false;
  }
}

export async function isSpeakerphoneOn(): Promise<boolean> {
  try {
    const r = await get()?.isSpeakerphoneOn();
    return r?.enabled ?? false;
  } catch {
    return false;
  }
}

/** Release the session when the call ends so the ringer behaves normally. */
export async function releaseAudioSession(): Promise<void> {
  try {
    await get()?.deactivate();
  } catch {
    /* the session frees itself when the app backgrounds */
  }
}
